import React, { useState, useEffect } from 'react';
import FormField from '../common/FormField';
import Button from '../common/Button';
import { 
  FirearmSpecificExerciseDTO, 
  ExerciseTypeDTO,
  ExerciseSpecificDTO 
} from '../../services/apiClient';
import { useExerciseSpecificApi } from '../../hooks/useExerciseSpecificApi';

interface FirearmSpecificExerciseFormProps {
  onSubmit: (exercise: FirearmSpecificExerciseDTO) => void;
  onCancel: () => void;
  initialValues?: FirearmSpecificExerciseDTO;
  availableExerciseTypes: ExerciseTypeDTO[]; // Add this prop
}

const FirearmSpecificExerciseForm: React.FC<FirearmSpecificExerciseFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  availableExerciseTypes
}) => {
  const [formData, setFormData] = useState<FirearmSpecificExerciseDTO>(initialValues || {});
  const [exerciseSpecifics, setExerciseSpecifics] = useState<ExerciseSpecificDTO[]>([]);
  const [selectedExerciseSpecifics, setSelectedExerciseSpecifics] = useState<ExerciseSpecificDTO[]>(
    initialValues?.exerciseSpecifics || []
  );

  const exerciseSpecificApi = useExerciseSpecificApi();

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load exercise specifics
        const specificsResponse = await exerciseSpecificApi.search({
          page: 1,
          pageSize: 9999,
          sortBy: 'DisplayPosition',
          sortDescending: false,
        });
        setExerciseSpecifics(specificsResponse?.data?.results || []);
      } catch (error) {
        console.error('Error loading exercise data:', error);
      }
    };

    loadData();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit({
      ...formData,
      exerciseSpecifics: selectedExerciseSpecifics
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormField
        id="exerciseTypeId"
        label="Exercise Type"
        type="dropdown"
        value={formData.exerciseTypeId || ''}
        onChange={(e) => setFormData({ ...formData, exerciseTypeId: e.target.value })}
        options={availableExerciseTypes.map(type => ({
          value: type.id as string,
          label: type.title || '',
        }))}
        placeholder="Select Exercise Type"
        disabled={!!initialValues} // Disable type selection when editing
      />

      <FormField
        id="exerciseSpecifics"
        label="Exercise Specifics"
        type="multiselect"
        value={selectedExerciseSpecifics.map(spec => spec.id as string)}
        onChange={(e) => {
          const selectedIds = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
          const selected = exerciseSpecifics.filter(spec => selectedIds.includes(spec.id as string));
          setSelectedExerciseSpecifics(selected);
        }}
        options={exerciseSpecifics.map(spec => ({
          value: spec.id as string,
          label: spec.title || '',
        }))}
        placeholder="Select Exercise Specifics"
      />

      <div className="flex justify-end space-x-4 mt-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{initialValues ? 'Update' : 'Add'} Exercise</Button>
      </div>
    </form>
  );
};

export default FirearmSpecificExerciseForm;